<template>
  <header class="a-page-head">
    <nav v-if="breadcrumbs && breadcrumbs.length" class="a-page-head__crumbs" aria-label="breadcrumb">
      <template v-for="(b, i) in breadcrumbs" :key="i">
        <span
          class="a-page-head__crumb"
          :class="{ 'is-current': i === breadcrumbs.length - 1 }"
        >{{ b }}</span>
        <span v-if="i < breadcrumbs.length - 1" class="a-page-head__crumb-sep">›</span>
      </template>
    </nav>

    <div class="a-page-head__row">
      <button
        v-if="back"
        class="a-page-head__back"
        type="button"
        :title="$t ? $t('btn.back') || 'Back' : 'Back'"
        @click="$emit('back')"
      >
        <i class="material-icons-round">arrow_back</i>
      </button>

      <div class="a-page-head__main">
        <h1 class="a-page-head__title">
          <i v-if="icon" class="material-icons-round a-page-head__icon">{{ icon }}</i>
          {{ title }}
        </h1>
        <p v-if="sub" class="a-page-head__sub">{{ sub }}</p>
      </div>

      <div v-if="$slots.actions" class="a-page-head__actions">
        <slot name="actions" />
      </div>
    </div>
  </header>
</template>

<script>
export default {
  name: 'APageHead',
  emits: ['back'],
  props: {
    title: { type: String, required: true },
    sub:   { type: String, default: '' },
    icon:  { type: String, default: '' },
    back:  { type: Boolean, default: false },
    breadcrumbs: { type: Array, default: () => [] },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/pineapple';

.a-page-head {
  // Full-bleed white strip — sits flush against the topbar.
  // Horizontal negative margin extends to .p-app__content's edges (which has padding: 0 24px 36px).
  background: $p-panel;
  border-bottom: 1px solid $p-line;
  padding: 24px 32px 20px;
  margin-top: 0;
  margin-right: -24px;
  margin-bottom: 0;
  margin-left: -24px;
  display: block;
}

.a-page-head__crumbs {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: $p-ink-soft;
  margin-bottom: 8px;
}

.a-page-head__crumb {
  font-weight: 500;

  &.is-current {
    color: $p-ink;
    font-weight: 700;
  }
}

.a-page-head__crumb-sep {
  color: $p-ink-softer;
}

.a-page-head__row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
}

.a-page-head__back {
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  background: $p-panel;
  border: 1px solid $p-line;
  border-radius: $p-radius-sm;
  color: $p-ink;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
  flex-shrink: 0;
  align-self: center;

  &:hover {
    background: $p-ink;
    color: #fff;
  }

  i { font-size: 18px; }
}

.a-page-head__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.a-page-head__title {
  margin: 0;
  font-size: 26px;
  font-weight: 900;
  letter-spacing: -0.025em;
  line-height: 1.1;
  color: $p-ink;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.a-page-head__icon {
  font-size: 26px !important;
  color: $p-point-deep;
}

.a-page-head__sub {
  margin: 0;
  font-size: 13px;
  color: $p-ink-mid;
}

.a-page-head__actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
</style>
